import { useEffect } from 'react'

// On phones, mobile.css shows each table row as a compact card: the row's name
// as a title line and a few key fields underneath. Tapping a card that has
// more fields opens it as a sheet from the bottom of the screen with every
// field and the row's buttons. CSS can't read header text or pick the key
// fields, so this hook marks the cells for it:
//
//   data-label        the column header, shown next to the value
//   data-mobile-role  lead | title | tail | key | more (where the cell goes)
//   data-span         cells spanning several columns (empty states, expanded
//                     detail rows) show full width with no label
//
// It watches the whole document so tables in dialogs and tables that
// re-render stay marked. A table can steer the choice with `data-mobile` on a
// header cell: "title", "key" (if any column says key, only those are keys)
// or "more".

const KEY_FIELDS = 3

// Headers that name the row, best first. The first match becomes the title.
const TITLE_PATTERNS = [
  /name/i,
  /^company$/i,
  /^customer$/i,
  /^flavor$/i,
  /^product$/i,
  /^ingredient$/i,
  /^item$/i,
  /^order (#|id)$/i,
  /^tote id$/i,
]

// Cells holding controls (switches, inputs, action buttons like "Print")
// aren't useful as a quick fact, so they go under "more" unless the table says
// otherwise. A button showing a number (e.g. an order count) still counts.
const CONTROL_SELECTOR = 'input, select, textarea, [role="switch"], [role="checkbox"]'

function hasControl(cell: Element): boolean {
  if (cell.querySelector(CONTROL_SELECTOR)) return true
  return Array.from(cell.querySelectorAll('button')).some(b => !/\d/.test(b.textContent ?? ''))
}

type Column = { label: string; hint: string | null }

function columns(table: HTMLTableElement): Column[] {
  const headRows = table.tHead?.rows
  const lastHeadRow = headRows?.[headRows.length - 1]
  if (!lastHeadRow) return []
  const cols: Column[] = []
  for (const cell of Array.from(lastHeadRow.cells)) {
    const label = (cell.textContent ?? '').replace(/[↑↓↕⇅]/g, '').trim()
    const hint = cell.getAttribute('data-mobile')
    for (let i = 0; i < cell.colSpan; i++) cols.push({ label, hint })
  }
  return cols
}

function titleColumn(cols: Column[]): number {
  const hinted = cols.findIndex(c => c.hint === 'title')
  if (hinted >= 0) return hinted
  for (const pattern of TITLE_PATTERNS) {
    const i = cols.findIndex(c => c.label && c.hint !== 'more' && pattern.test(c.label))
    if (i >= 0) return i
  }
  return -1
}

function setAttr(el: Element, name: string, value: string | null) {
  if (value === null) {
    if (el.hasAttribute(name)) el.removeAttribute(name)
  } else if (el.getAttribute(name) !== value) {
    el.setAttribute(name, value)
  }
}

function labelTable(table: HTMLTableElement) {
  const cols = columns(table)
  setAttr(table, 'data-mobile-cards', cols.length > 0 ? '' : null)
  if (cols.length === 0) return
  const hintedKeys = cols.some(c => c.hint === 'key')
  const titleCol = titleColumn(cols)

  for (const body of Array.from(table.tBodies)) {
    for (const row of Array.from(body.rows)) {
      const cells = Array.from(row.cells)
      // Fall back to the first labelled cell without controls as the title.
      let rowTitle = titleCol
      if (rowTitle < 0) {
        let col = 0
        for (const cell of cells) {
          if (cols[col]?.label && !hasControl(cell)) {
            rowTitle = col
            break
          }
          col += cell.colSpan
        }
      }

      // Rows with text boxes are being edited in place: keep every field
      // showing so nothing editable is tucked away.
      const editable = !!row.querySelector('input:not([type="checkbox"]):not([type="hidden"]), select, textarea')

      let col = 0
      let keys = 0
      let more = 0
      let seenTitle = false
      for (const cell of cells) {
        const spansRow = cell.colSpan > 1 || cells.length === 1
        const { label, hint } = cols[col] ?? { label: '', hint: null }
        let role: string | null = null
        if (!spansRow) {
          if (col === rowTitle) {
            role = 'title'
            seenTitle = true
          } else if (!label) {
            role = seenTitle ? 'tail' : 'lead'
          } else if (
            hint !== 'more' &&
            (hintedKeys ? hint === 'key' : keys < KEY_FIELDS && !hasControl(cell))
          ) {
            role = 'key'
            keys++
          } else {
            role = 'more'
            more++
          }
        }
        setAttr(cell, 'data-span', spansRow ? '' : null)
        setAttr(cell, 'data-label', spansRow || !label ? null : label)
        setAttr(cell, 'data-mobile-role', role)
        col += cell.colSpan
      }
      setAttr(row, 'data-more', more > 0 && !editable ? String(more) : null)
      // Rows with their own tap action (Orders opens the line items) keep it;
      // their sheet opens from the Details button on the card instead.
      setAttr(row, 'data-own-action', row.classList.contains('cursor-pointer') ? '' : null)
      if (editable) setAttr(row, 'data-expanded', '')
    }
  }
}

// ── Card sheet ──────────────────────────────────────────────────────────────
// The open card itself becomes the sheet (mobile.css pins it to the bottom of
// the screen), so its buttons, switches and menus keep working as they are.

const PHONE = '(max-width: 639px)'
const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="switch"], [role="combobox"], [contenteditable="true"]'
// Things that open above the sheet (menus, confirmations, toasts) and so count
// as part of it.
const OVERLAYS =
  '[data-radix-popper-content-wrapper], [role="menu"], [role="listbox"], [role="alertdialog"], [data-sonner-toaster], [data-sonner-toast]'

let sheetRow: HTMLTableRowElement | null = null

function openSheet(row: HTMLTableRowElement) {
  closeSheet()
  sheetRow = row
  // Keep the card's space in the list so nothing jumps behind the sheet.
  row.parentElement?.style.setProperty('--mobile-sheet-gap', `${row.offsetHeight}px`)
  setAttr(row, 'data-sheet', '')
  document.documentElement.setAttribute('data-mobile-sheet', '')
}

function closeSheet() {
  const row = sheetRow
  sheetRow = null
  document.documentElement.removeAttribute('data-mobile-sheet')
  if (!row) return
  setAttr(row, 'data-sheet', null)
  row.parentElement?.style.removeProperty('--mobile-sheet-gap')
}

function insideSheet(target: Element, row: HTMLTableRowElement): boolean {
  if (row.contains(target) || target.closest(OVERLAYS)) return true
  // A dialog opened from the sheet (e.g. Draft email) sits above it.
  const dialog = target.closest('[role="dialog"]')
  return !!dialog && !dialog.contains(row)
}

function onPointerDown(event: PointerEvent) {
  const target = event.target
  if (!sheetRow || !(target instanceof Element)) return
  // Taps on the dimmed page close the sheet and do nothing else.
  if (!insideSheet(target, sheetRow)) {
    event.stopPropagation()
    event.preventDefault()
  }
}

function onClick(event: MouseEvent) {
  const target = event.target
  if (!(target instanceof Element)) return

  if (sheetRow) {
    const row = sheetRow
    if (!insideSheet(target, row) || target === row) {
      // Outside the sheet, or its Done strip (the row's ::after).
      event.stopPropagation()
      event.preventDefault()
      closeSheet()
    } else if (
      target.closest('[role="menuitem"]') ||
      (row.contains(target) && target.closest('a, button:not([aria-haspopup]):not([role])'))
    ) {
      // An action ran: let it, then put the card back.
      setTimeout(closeSheet, 0)
    }
    return
  }

  if (!window.matchMedia(PHONE).matches) return
  const row = target.closest('tr')
  if (!row || !row.hasAttribute('data-more') || !row.closest('table')?.hasAttribute('data-mobile-cards')) return
  if (target !== row) {
    // Buttons, links and boxes on the card do their own thing, and so does a
    // row with its own tap action (its sheet opens from Details).
    if (target.closest(INTERACTIVE) || row.hasAttribute('data-own-action')) return
  }
  event.stopPropagation()
  event.preventDefault()
  openSheet(row)
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' && sheetRow) {
    event.stopPropagation()
    closeSheet()
  }
}

export function useMobileTableLabels() {
  useEffect(() => {
    let frame = 0
    const run = () => {
      frame = 0
      document.querySelectorAll('table').forEach(labelTable)
      // The open card was re-rendered away, or the screen got wider.
      if (sheetRow && (!sheetRow.isConnected || !window.matchMedia(PHONE).matches)) closeSheet()
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(run)
    }
    schedule()
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true, characterData: true })
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('click', onClick, true)
    document.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('resize', schedule)
    return () => {
      observer.disconnect()
      closeSheet()
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('resize', schedule)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])
}
